import { Vector3 } from 'three';
import { ITexture } from '../../@types/ContextProviderRelated';
import Model2DModel from '../../models/standard/Model2DModel';
import { IObject } from '../../models/standard/ObjectModel';
import filesApiService from '../filesApiService';
import { parseFilesApiModelRequest } from '../jsonParser';
import { createAttributes, createObject } from '../transformation/tranformToMultiRoomBuilding';
import { fetchTextureFromApi } from './fetchTextureFromApi';

// Interface of the model2D instantiation object
export interface Model2DInstantiation {
  model2D: Model2DModel | null;
  modelData: any;
  modelFetchingSuccesful: boolean;
  neededToFetchFromFilesApi: boolean;
  neededToFetchTexture: boolean;
  fetchingSuccesful: boolean;
  textureFetchingSuccesful: boolean;
  modelId: string;
  textureId: string;
}

/**
 * Helper: Fetch full 2D model data by ID and instantiate a Model2DModel.
 * Also handles texture fetching.
 */
export async function instantiateModel2DAsync(id: number, position: Vector3, rotation: number, textureInput: ITexture, rawModelData: any): Promise<Model2DInstantiation> {
  try {
    let texture: ITexture | null = null;
    let neededToFetchTexture = false;
    let textureFetchingSuccesful = true;

    // Texture handling (unchanged)
    if (textureInput.id === -1 && textureInput.color) {
      texture = textureInput;
    } else {
      neededToFetchTexture = true;
      const textureResult = await fetchTextureFromApi(textureInput);
      texture = (textureResult.texture ?? null) as ITexture | null;
      textureFetchingSuccesful = textureResult.fetchingSuccesful || !textureResult.neededToFetchTexture;
    }

    // --- NEW: only get dimensions + svg from Files API, keep meta / attributes from rawModelData ---

    const response = await filesApiService.dataRequest(`model/${id}`);
    const modelData = parseFilesApiModelRequest(response);

    const svgPath = await filesApiService.getSVGFileFromModel(modelData.id);

    const base = rawModelData;

    const length = modelData.length ?? base.dimensions?.length ?? 0;
    const depth = modelData.depth ?? base.dimensions?.depth ?? 0;

    const attributes = createAttributes(base.attributes);
    const object = createObject(base.object) as IObject;

    const model2D = new Model2DModel(
      base.id ?? modelData.id,
      base.name ?? modelData.name,
      svgPath || base.filePath,
      base.fileFormat ?? modelData.fileFormat,
      base.fileSize ?? modelData.fileSize,
      length,
      depth,
      object,
      position,
      rotation,
      texture,
      attributes,
    );

    if (base.uuid) {
      model2D.setUUID(base.uuid);
    }

    return {
      model2D,
      modelData: rawModelData,
      modelFetchingSuccesful: true,
      neededToFetchFromFilesApi: true,
      neededToFetchTexture,
      fetchingSuccesful: textureFetchingSuccesful,
      textureFetchingSuccesful,
      modelId: String(id),
      textureId: String(textureInput.id),
    };
  } catch (error) {
    console.error('Error instantiating 2D model by ID:', id, error);

    return {
      model2D: null,
      modelData: rawModelData,
      modelFetchingSuccesful: false,
      neededToFetchFromFilesApi: true,
      neededToFetchTexture: true,
      fetchingSuccesful: false,
      textureFetchingSuccesful: false,
      modelId: String(id),
      textureId: String(textureInput.id),
    };
  }
}
